// Drives the access case's Billing Identity drawer
// (partners/provider_access_cases/_change_billing_identity_drawer).
//
// Two ways to name a new billing identity, and the form must submit exactly
// one signed candidate_token for whichever is active:
//
//   * the EIN-search autocomplete (layouts/_autocomplete_field). Its hidden id
//     field IS a candidate_token input; stimulus-autocomplete writes the picked
//     option's token into it and the shared autocomplete-field controller blanks
//     it again (loadstart) as soon as the operator types a new query.
//   * the "Other" card, which carries its own hidden candidate_token holding the
//     server-minted manual-entry token plus the NPI/EIN inputs.
//
// Only one of the two inputs is ever enabled with a value, so the server sees a
// single candidate_token no matter how a form encoder orders duplicate names.
// The server re-verifies the token and re-validates the typed NPI/EIN; nothing
// here is a trust boundary.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["searchToken", "otherCard", "otherCheckbox", "otherToken", "manualFields", "manualNpi", "manualEin", "submit"];
  static values = {
    // Mirrors _provider_result_card's `selected:` styling, toggled on the Other card.
    selectedClasses: { type: String, default: "border-core-purple-500 bg-[#FCFCFF] shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]" },
    unselectedClasses: { type: String, default: "border-support-light-300" },
  };

  // A 422 re-render comes back with Other already selected and whatever was
  // typed still in its inputs, so derive the button state from the DOM rather
  // than assuming a fresh drawer.
  connect() {
    // Other owning the submission also means the search half must be inert, or
    // a re-rendered drawer would post an empty second candidate_token.
    if (this.otherSelected()) this.clearSearchSelection();
    this.updateSubmit();
  }

  // The hidden candidate_token the autocomplete owns changed: either a result
  // was picked (a token landed in it) or the operator started typing again and
  // autocomplete-field#clear blanked it. A pick wins over Other; a blank only
  // takes the submit button away.
  searchTokenChanged() {
    if (this.searchTokenValue()) {
      this.deselectOther();
      this.searchTokenTarget.disabled = false;
    }
    this.updateSubmit();
  }

  // The bubbling stimulus-autocomplete event, wired on the form element. The
  // hidden field's own change event (above) already covers the pick; this keeps
  // the two in sync even if a future option shape stops writing the hidden field.
  picked() {
    this.searchTokenChanged();
  }

  // Clicking the Other card (anywhere but its checkbox) toggles it. Selecting it
  // takes the search's token away entirely (blanked AND disabled) so the typed
  // identity is the only one that can be submitted. Clicks on the checkbox or its
  // label are left to the checkbox's own change event (otherToggled), because the
  // browser has already flipped `checked` by the time the click bubbles here;
  // toggling again would undo a keyboard Space press.
  chooseOther(event) {
    if (event?.target?.closest?.("label, input[type='checkbox']")) return;

    if (this.otherSelected()) {
      this.deselectOther();
    } else {
      this.selectOther();
    }
    this.updateSubmit();
  }

  // The Other checkbox changed natively (mouse on the checkbox/label, or Space
  // from the keyboard): follow the state the browser already applied.
  otherToggled() {
    if (this.otherSelected()) {
      this.selectOther();
    } else {
      this.deselectOther();
    }
    this.updateSubmit();
  }

  // Typing in the NPI/EIN inputs only re-checks whether Other is submittable.
  manualInput() {
    this.updateSubmit();
  }

  // Clicks inside the Other card's inputs must not toggle the card itself.
  stopPropagation(event) {
    event.stopPropagation();
  }

  selectOther() {
    this.clearSearchSelection();
    this.markOther(true);
    if (this.hasOtherTokenTarget) this.otherTokenTarget.disabled = false;
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.classList.remove("hidden");
      this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = false; });
      this.manualFieldsTarget.querySelector("input")?.focus();
    }
  }

  deselectOther() {
    this.markOther(false);
    if (this.hasOtherTokenTarget) this.otherTokenTarget.disabled = true;
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.classList.add("hidden");
      this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = true; });
    }
  }

  // Drop both halves of a search selection: the signed token and the name left
  // in the visible box, so the drawer never shows an organization it won't send.
  clearSearchSelection() {
    if (this.hasSearchTokenTarget) {
      this.searchTokenTarget.value = "";
      this.searchTokenTarget.disabled = true;
    }
    const input = this.element.querySelector("[data-autocomplete-target='input']");
    if (input) input.value = "";
  }

  markOther(selected) {
    if (this.hasOtherCheckboxTarget) this.otherCheckboxTarget.checked = selected;
    if (!this.hasOtherCardTarget) return;

    const card = this.otherCardTarget;
    card.setAttribute("aria-selected", selected ? "true" : "false");
    const on = this.selectedClassesValue.split(" ").filter(Boolean);
    const off = this.unselectedClassesValue.split(" ").filter(Boolean);
    card.classList.remove(...(selected ? off : on));
    card.classList.add(...(selected ? on : off));
  }

  otherSelected() {
    return this.hasOtherCheckboxTarget && this.otherCheckboxTarget.checked;
  }

  searchTokenValue() {
    return this.hasSearchTokenTarget ? this.searchTokenTarget.value : "";
  }

  // Other needs a complete 10-digit NPI and 9-digit EIN (the server enforces
  // the same thing); a searched identity needs only its token.
  submittable() {
    if (this.otherSelected()) {
      const npi = this.hasManualNpiTarget ? this.digits(this.manualNpiTarget) : "";
      const ein = this.hasManualEinTarget ? this.digits(this.manualEinTarget) : "";
      return npi.length === 10 && ein.length === 9;
    }
    return Boolean(this.searchTokenValue());
  }

  digits(target) {
    return (target?.value ?? "").replace(/\D/g, "");
  }

  updateSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !this.submittable();
  }
}
